import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Bot, User, Clock, CheckCircle2,
  ArrowLeft, X, RefreshCw, Sparkles, Hand, ArrowRightLeft,
  FileText, Search, HelpCircle, ExternalLink, Radio, Zap, Settings,
  BookOpen, Wand2, PhoneForwarded, Phone, Link2, UserPlus, AlertCircle,
} from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import HelpTooltip from '@/components/help/HelpTooltip';
import { ChannelIcon, CHANNEL_META } from '@/components/ChannelIcon';

// Incidente 2026-10-03 (pool de DB agotado): una conversación con la fila
// bloqueada hacía que cada POST /read y cada «Tomar conversación» se quedara
// colgado; el sondeo de 5 s seguía lanzando más encima de los colgados y el
// botón se podía pulsar en bucle (30 take-control con 500 en minutos). Reglas:
//   * acciones y /read: MÁXIMO 1 reintento, con espera (Retry-After o 2 s), y
//     solo ante 409/502/503/504 o fallo de red;
//   * el sondeo no se encima: si la vuelta anterior sigue en vuelo, se salta, y
//     tras un fallo espera cada vez más (5 s → 10 → 20 … hasta 60 s).
const _esperar = (ms) => new Promise((r) => setTimeout(r, ms));
const _REINTENTABLES = new Set([409, 502, 503, 504]);
async function postConUnReintento(url, body, config) {
  try {
    return await api.post(url, body, config);
  } catch (e) {
    const status = e?.response?.status;
    if (e?.response && !_REINTENTABLES.has(status)) throw e;
    const retryAfter = Number(e?.response?.headers?.['retry-after']);
    const ms = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 2000;
    await _esperar(Math.min(ms, 10000));
    return api.post(url, body, config); // segundo y último intento
  }
}
const POLL_MS = 5000;
const POLL_MAX_BACKOFF_MS = 60000;

const STATUS_CONFIG = {
  new: { label: 'Nuevo', color: 'bg-blue-100 text-blue-700' },
  waiting_agent: { label: 'Sin responder', color: 'bg-amber-100 text-amber-700' },
  waiting_customer: { label: 'Tú respondiste', color: 'bg-emerald-100 text-emerald-700' },
  ai_handling: { label: 'IA atendiendo', color: 'bg-violet-100 text-violet-700' },
  closed: { label: 'Cerrado', color: 'bg-gray-100 text-gray-500' },
};

const ATTENDED_CONFIG = {
  pending: { label: 'Sin responder', color: 'bg-red-50 text-red-600 border-red-200' },
  ai_handling: { label: 'IA', color: 'bg-violet-50 text-violet-600 border-violet-200' },
  human_handling: { label: 'Equipo', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  closed: { label: 'Cerrado', color: 'bg-gray-50 text-gray-500 border-gray-200' },
};

const FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'mine', label: 'Mis chats' },
  { key: 'unattended', label: 'Sin atender' },
  { key: 'ai', label: 'IA atendiendo' },
];

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${c.color}`}>{c.label}</span>;
}

function AttendedBadge({ attended }) {
  const c = ATTENDED_CONFIG[attended] || ATTENDED_CONFIG.pending;
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium border ${c.color}`}>{c.label}</span>;
}

/* ── Stats Cards ───────────────────────────────────────────── */
function StatsBar({ stats, onFilter }) {
  if (!stats) return null;
  const cards = [
    { key: 'new_today', label: 'Nuevas hoy', value: stats.new_today, filter: null, icon: Zap },
    { key: 'attended_team', label: 'Atendidas', value: stats.attended_team, filter: null, icon: CheckCircle2 },
    { key: 'ai_only', label: 'Solo IA', value: stats.ai_only, filter: 'ai', icon: Bot },
    { key: 'unattended', label: 'Sin respuesta', value: stats.unattended, filter: 'unattended', icon: Clock },
  ];
  return (
    <div className="grid grid-cols-4 gap-2 px-4 py-2 border-b border-[#E2E8F0] bg-[#F8FAFC]" data-testid="inbox-stats">
      {cards.map(c => (
        <button key={c.key} onClick={() => c.filter && onFilter(c.filter)}
          className="text-center p-2 rounded-lg hover:bg-white transition-colors" data-testid={`stat-${c.key}`}>
          <p className="text-lg font-bold text-[#0F172A]">{c.value}</p>
          <p className="text-[10px] text-[#64748B]">{c.label}</p>
        </button>
      ))}
    </div>
  );
}

/* ── Conversation List ─────────────────────────────────────── */
// Sprint 58.1 — canales que el tenant puede filtrar en el panel; la lista
// se muestra completa aunque hoy solo WhatsApp tenga tráfico real, para que
// el filtro ya esté listo cuando Messenger/Instagram (Sprint 58.3) empiecen
// a alimentar conversaciones.
// Hotfix (07-18): instagram_wall/facebook_wall (comentarios de muro, Sprint
// 58.3c) nunca se agregaron aquí aunque CHANNEL_META ya los soporta — se
// quedaban sin chip propio y solo aparecían bajo "Todos".
const FILTERABLE_CHANNELS = ['whatsapp', 'messenger', 'instagram', 'instagram_wall', 'facebook_wall', 'web_chat', 'voice'];

function ChannelFilterBar({ channelFilter, setChannelFilter }) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[#E2E8F0] overflow-x-auto" data-testid="channel-filter-bar">
      <button onClick={() => setChannelFilter('')}
        className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${!channelFilter ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'}`}
        data-testid="channel-filter-all">Todos</button>
      {FILTERABLE_CHANNELS.map(ch => (
        <button key={ch} onClick={() => setChannelFilter(ch)} title={CHANNEL_META[ch]?.label}
          className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border transition-colors ${channelFilter === ch ? 'border-[#0F172A] bg-[#F1F5F9]' : 'border-transparent hover:bg-[#F8FAFC]'}`}
          data-testid={`channel-filter-${ch}`}>
          <ChannelIcon channel={ch} active className="w-4 h-4" />
        </button>
      ))}
    </div>
  );
}

// Fila de iconos de canal para una tarjeta de conversación agrupada — solo
// el canal activo (el más reciente) se ilumina con su color de marca, el
// resto se muestra apagado para no competir visualmente.
function ChannelChipRow({ channels, activeChannel }) {
  const seen = [...new Set(channels.map(c => c.channel))];
  return (
    <div className="flex items-center gap-1 mt-1">
      {seen.map(ch => <ChannelIcon key={ch} channel={ch} active={ch === activeChannel} className="w-3 h-3" />)}
    </div>
  );
}

function ConversationList({ items, selected, onSelect, loading, onRefresh, filter, setFilter, search, setSearch, total, hasMore, onLoadMore, stats, onStatsFilter, isOwnerAdmin, channelFilter, setChannelFilter }) {
  return (
    <div className="flex flex-col h-full" data-testid="conversation-list">
      {isOwnerAdmin && <StatsBar stats={stats} onFilter={onStatsFilter} />}
      <ChannelFilterBar channelFilter={channelFilter} setChannelFilter={setChannelFilter} />
      {/* Filters */}
      <div className="px-3 py-2 border-b border-[#E2E8F0] space-y-2">
        <div className="flex gap-1 overflow-x-auto">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${filter === f.key ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'}`}
              data-testid={`filter-${f.key}`}>{f.label}</button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o teléfono..."
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-[#E2E8F0] rounded-lg" data-testid="conversation-search" />
        </div>
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#E2E8F0]">
        <span className="text-xs text-[#64748B]">{total} conversaciones</span>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4" data-testid="empty-conversations">
            <MessageSquare className="w-10 h-10 text-[#CBD5E1] mb-3" strokeWidth={1.5} />
            <p className="text-sm font-medium text-[#64748B]">No tienes conversaciones aún</p>
            <p className="text-xs text-[#94A3B8] mt-1 mb-4">Si conectaste WhatsApp en Integraciones, los mensajes de tus captaciones aparecerán aquí.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => window.location.href='/tenant/integrations'} data-testid="go-integrations-btn">Ir a Integraciones</Button>
              <Button size="sm" variant="outline" onClick={() => window.location.href='/tenant/marketing/lead-capture'} data-testid="go-capture-btn">Ir a Captación</Button>
            </div>
          </div>
        )}
        {items.map(c => (
          <button key={c.group_key} onClick={() => onSelect(c)} data-testid={`conversation-item-${c.group_key}`}
            className={`w-full text-left px-4 py-3 border-b border-[#F1F5F9] transition-colors hover:bg-[#F8FAFC] ${selected?.group_key === c.group_key ? 'bg-[#F1F5F9]' : ''}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#F1F5F9] flex items-center justify-center flex-shrink-0 text-xs font-bold text-[#64748B]">
                  {(c.contact_name || c.phone_normalized || '?')[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-[#0F172A] truncate">{c.contact_name || c.phone_normalized || 'Contacto'}</span>
                    {c.unread_count > 0 && <span className="w-4 h-4 rounded-full bg-[#F72585] text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">{c.unread_count}</span>}
                  </div>
                  <p className="text-xs text-[#64748B] truncate mt-0.5">{c.last_message_preview || 'Sin mensajes'}</p>
                  <ChannelChipRow channels={c.channels} activeChannel={c.active_channel} />
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {c.crm_lead_id && <a href={`/tenant/crm/contacts/${c.crm_lead_id}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[9px] rounded border border-blue-200 hover:bg-blue-100" data-testid="lead-chip"
                      onClick={e => e.stopPropagation()}><ExternalLink className="w-2.5 h-2.5" /> Lead</a>}
                    {c.interest_label && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[9px] rounded border border-amber-200 truncate max-w-[100px]">{c.interest_label}</span>}
                    {c.assigned_user_name && <span className="text-[9px] text-[#94A3B8]">{c.assigned_user_name}</span>}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <AttendedBadge attended={c.attended_status} />
                {c.last_message_at && <span className="text-[10px] text-[#94A3B8]">{new Date(c.last_message_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>}
              </div>
            </div>
          </button>
        ))}
        {hasMore && <div className="px-4 py-3"><Button variant="outline" size="sm" className="w-full" onClick={onLoadMore} data-testid="load-more-btn">Cargar más</Button></div>}
      </div>
    </div>
  );
}

/* ── Message Bubble ────────────────────────────────────────── */
function MessageBubble({ msg }) {
  const navigate = useNavigate();
  const isInbound = msg.direction === 'inbound';
  const isSystem = msg.sender_type === 'system';
  const isAiReply = !isInbound && msg.sender_type === 'ai';

  const goFaq = async () => {
    try {
      const { data } = await api.get(`/tenant/inbox/learning-prefill/faq?message_id=${msg.id}`);
      navigate('/tenant/ai/knowledge', { state: { faqPrefill: data, tab: 'upload' } });
      toast.success('Datos listos — revisa y guarda la FAQ en Biblioteca');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const goSkill = async () => {
    try {
      const { data } = await api.get(`/tenant/inbox/learning-prefill/skill?message_id=${msg.id}`);
      navigate('/tenant/ai/skills', { state: { skillPrefill: data, tab: 'edit' } });
      toast.success('Datos listos — revisa y guarda la habilidad');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (isSystem) {
    return <div className="flex justify-center mb-2"><span className="text-[10px] text-[#94A3B8] bg-[#F1F5F9] px-3 py-1 rounded-full">{msg.body_text}</span></div>;
  }

  return (
    <div className={`flex ${isInbound ? 'justify-start' : 'justify-end'} mb-2`}>
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
        isInbound ? 'bg-white border border-[#E2E8F0] text-[#0F172A]'
        : msg.sender_type === 'ai' ? 'bg-violet-50 border border-violet-200 text-[#0F172A]'
        : 'bg-[#0F172A] text-white'
      }`}>
        {msg.sender_type !== 'contact' && (
          <div className={`flex items-center gap-1 text-[10px] mb-1 ${isInbound ? 'text-[#94A3B8]' : 'text-white/60'}`}>
            {msg.sender_type === 'ai' ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}
            <span>{msg.sender_type === 'ai' ? 'IA' : (msg.sender_name || 'Agente')}</span>
          </div>
        )}
        <p className="text-sm whitespace-pre-wrap">{msg.body_text}</p>
        {isAiReply && (
          <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-violet-200/60">
            <button type="button" onClick={goFaq} title="Crea una pregunta frecuente en Biblioteca con esta respuesta"
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-white border border-violet-200 text-violet-700 hover:bg-violet-100"
              data-testid={`convert-faq-${msg.id}`}>
              <BookOpen className="w-3 h-3" /> Convertir en FAQ
            </button>
            <button type="button" onClick={goSkill} title="Abre el constructor de habilidades con el contexto de este mensaje"
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-white border border-violet-200 text-violet-700 hover:bg-violet-100"
              data-testid={`create-skill-${msg.id}`}>
              <Wand2 className="w-3 h-3" /> Crear Skill
            </button>
            <HelpTooltip text="Estos botones copian el mensaje real a Biblioteca o Habilidades para que la IA repita buenas respuestas. No publican nada automáticamente: tú revisas antes de guardar." />
          </div>
        )}
        <p className={`text-[10px] mt-1 ${isInbound ? 'text-[#94A3B8]' : 'text-white/50'}`}>
          {msg.created_at && new Date(msg.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
}

/* ── Transfer Modal ────────────────────────────────────────── */
function TransferModal({ open, onClose, conversationId, onDone }) {
  const [team, setTeam] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/tenant/business/team/eligible').then(r => setTeam(r.data || [])).catch(() => {});
    }
  }, [open]);

  const handleTransfer = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await api.post(`/tenant/inbox/conversations/${conversationId}/transfer`, { to_user_id: selected, note: note || undefined });
      toast.success('Conversación transferida');
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="transfer-modal">
        <DialogHeader><DialogTitle className="text-lg font-bold text-[#0F172A]">Pasar a vendedor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-[#64748B]">Selecciona al compañero que recibirá esta conversación</p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {team.map(m => (
              <button key={m.id} onClick={() => setSelected(m.id)} data-testid={`transfer-option-${m.id}`}
                className={`w-full text-left p-2.5 rounded-lg border transition-all text-sm ${selected === m.id ? 'border-[#0F172A] bg-[#F8FAFC] ring-1 ring-[#0F172A]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`}>
                <span className="font-medium text-[#0F172A]">{m.name}</span>
                <span className="text-xs text-[#94A3B8] ml-2">{m.whatsapp_phone || 'Sin WhatsApp'}</span>
              </button>
            ))}
            {team.length === 0 && <p className="text-xs text-[#94A3B8] text-center py-4">No hay vendedores elegibles</p>}
          </div>
          <div>
            <label className="text-xs text-[#64748B]">Nota para el compañero (opcional)</label>
            <input value={note} onChange={e => setNote(e.target.value.slice(0, 200))} placeholder="Ej: Cliente interesado en el modelo X"
              className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="transfer-note" />
          </div>
          <Button onClick={handleTransfer} disabled={!selected || loading} className="w-full bg-[#0F172A] text-white" data-testid="confirm-transfer-btn">
            {loading ? 'Transfiriendo...' : 'Pasar conversación'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Summary Panel ─────────────────────────────────────────── */
function SummaryPanel({ conversationId, extraData }) {
  const [summary, setSummary] = useState(extraData?.summary_text || '');
  const [generatedAt, setGeneratedAt] = useState(extraData?.summary_generated_at || '');
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/tenant/inbox/conversations/${conversationId}/summarize`);
      setSummary(data.summary_text || '');
      setGeneratedAt(data.generated_at || '');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="border-t border-[#E2E8F0] p-3 bg-[#FAFBFC]" data-testid="summary-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-[#0F172A] flex items-center gap-1"><FileText className="w-3 h-3" /> Resumen IA</span>
        <Button variant="ghost" size="sm" onClick={generate} disabled={loading} data-testid="generate-summary-btn">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> <span className="ml-1 text-xs">{summary ? 'Actualizar' : 'Generar'}</span>
        </Button>
      </div>
      {summary ? (
        <div>
          <p className="text-xs text-[#0F172A] whitespace-pre-wrap">{summary}</p>
          {generatedAt && <p className="text-[10px] text-[#94A3B8] mt-1">{new Date(generatedAt).toLocaleString('es-MX')}</p>}
        </div>
      ) : (
        <p className="text-xs text-[#94A3B8]">{loading ? 'Generando resumen...' : 'Presiona generar para obtener un resumen de esta conversación'}</p>
      )}
    </div>
  );
}

/* ── Chat Panel ────────────────────────────────────────────── */
// Sprint 58.1 — la tarjeta de la izquierda ahora es un CONTACTO con uno o
// más hilos (canales) adentro; este panel recibe el grupo completo y
// mantiene su propia pestaña de canal activo, en vez de recibir una sola
// conversación como antes. El resto de la lógica de abajo sigue operando
// sobre un objeto `conversation` derivado (canal activo + datos del
// contacto), para no reescribir cada acción existente.
function ChannelTabs({ channels, activeId, onSelect }) {
  if (channels.length < 2) return null;
  return (
    <div className="flex items-center gap-1 px-4 pt-2 border-b border-[#E2E8F0] bg-white overflow-x-auto" data-testid="channel-tabs">
      {channels.map(ch => (
        <button key={ch.conversation_id} onClick={() => onSelect(ch.conversation_id)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border-b-2 whitespace-nowrap ${
            activeId === ch.conversation_id ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#94A3B8] hover:text-[#64748B]'
          }`} data-testid={`channel-tab-${ch.channel}`}>
          <ChannelIcon channel={ch.channel} active className="w-3.5 h-3.5" />
          {CHANNEL_META[ch.channel]?.label || ch.channel}
          {ch.unread_count > 0 && <span className="w-3.5 h-3.5 rounded-full bg-[#F72585] text-white text-[8px] font-bold flex items-center justify-center">{ch.unread_count}</span>}
        </button>
      ))}
    </div>
  );
}

// Sprint 58.1 (hotfix) — antes vivía en su propia pantalla (InboxCalls.js,
// /tenant/inbox/calls); ahora que una llamada es un canal más dentro de la
// tarjeta del contacto, este panel de solo lectura reemplaza el hilo de
// mensajes cuando la pestaña activa es "voice". Reutiliza el mismo endpoint
// de detalle que ya existía (/tenant/voice/calls/{id}), incluida la
// transcripción.
const CALL_STATUS_CONFIG = {
  ringing: { label: 'Sonando', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'En curso', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Completada', color: 'bg-gray-100 text-gray-700' },
  failed: { label: 'Fallida', color: 'bg-red-100 text-red-700' },
  missed: { label: 'Perdida', color: 'bg-amber-100 text-amber-700' },
};

// Buscador de contactos para atribuir una llamada suelta (por ejemplo, una
// llamada entrante a tu número general, que llega sin contacto asociado).
// Solo owner/admin pueden usarlo — el backend rechaza al resto.
function LinkLeadModal({ open, onClose, callId, onLinked }) {
  const [search, setSearch] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  // Al cerrar se limpia todo para que la próxima vez abra en blanco.
  useEffect(() => {
    if (!open) { setSearch(''); setLeads([]); setSelected(null); setError(''); }
  }, [open]);

  // Carga perezosa: la lista solo se pide cuando el buscador está abierto.
  // Sin texto muestra los contactos más recientes; con texto busca (con una
  // pequeña espera para no consultar en cada tecla).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true); setError('');
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ status: 'all', page: '1', page_size: '10' });
      const q = search.trim();
      if (q) params.set('search', q);
      api.get(`/tenant/crm/leads?${params.toString()}`)
        .then(({ data }) => { if (!cancelled) setLeads(data?.items || []); })
        .catch(e => {
          if (cancelled) return;
          setLeads([]);
          setError(formatApiError(e.response?.data?.detail) || 'No se pudo cargar tu lista de contactos.');
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, search ? 350 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, search]);

  const handleLink = async () => {
    if (!selected || saving) return;
    const name = selected.display_name || 'el contacto';
    setSaving(true);
    try {
      await api.post(`/tenant/voice/calls/${callId}/link-lead`, { crm_lead_id: selected.id });
      setSaving(false);
      onClose();
      toast.success(`Llamada guardada en el historial de ${name}`);
      onLinked?.();
    } catch (e) {
      setSaving(false);
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo vincular la llamada.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="link-lead-modal">
        <DialogHeader><DialogTitle className="text-lg font-bold text-[#0F172A]">Vincular llamada a un contacto</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-start gap-1.5">
            <p className="text-xs text-[#64748B] flex-1">
              Busca a la persona que llamó por su nombre, teléfono o correo. La llamada, su resumen y su grabación quedarán guardados en la ficha de ese contacto.
            </p>
            <HelpTooltip text="Las llamadas a tu número general llegan sin dueño: el sistema no sabe quién habló. Al vincularla, la llamada aparece en el historial del contacto y tu equipo puede darle seguimiento desde su ficha. Puedes cambiar el contacto después si te equivocas." />
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} autoFocus
              placeholder="Buscar por nombre, teléfono o correo..."
              className="pl-8 text-sm" data-testid="link-lead-search" />
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="link-lead-results">
            {loading && (
              <div className="flex items-center justify-center py-6" data-testid="link-lead-loading">
                <RefreshCw className="w-4 h-4 text-[#94A3B8] animate-spin" />
              </div>
            )}
            {!loading && error && (
              <p className="text-xs text-red-600 text-center py-4" data-testid="link-lead-error">{error}</p>
            )}
            {!loading && !error && leads.length === 0 && (
              <p className="text-xs text-[#94A3B8] text-center py-4" data-testid="link-lead-empty">
                {search.trim() ? 'Ningún contacto coincide con esa búsqueda.' : 'Sin contactos todavía.'}
              </p>
            )}
            {!loading && !error && leads.map(l => (
              <button key={l.id} type="button" onClick={() => setSelected(l)} data-testid={`link-lead-option-${l.id}`}
                className={`w-full text-left p-2.5 rounded-lg border transition-all ${selected?.id === l.id ? 'border-[#0F172A] bg-[#F8FAFC] ring-1 ring-[#0F172A]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`}>
                <span className="block text-sm font-medium text-[#0F172A] truncate">{l.display_name || 'Sin nombre'}</span>
                <span className="block text-xs text-[#94A3B8] truncate">
                  {[l.phone, l.email].filter(Boolean).join(' · ') || 'Sin teléfono ni correo'}
                </span>
              </button>
            ))}
          </div>
          <Button onClick={handleLink} disabled={!selected || saving} className="w-full bg-[#0F172A] text-white" data-testid="confirm-link-lead-btn">
            {saving ? 'Vinculando...' : 'Vincular llamada'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CallDetailPanel({ callId, isOwnerAdmin, onLeadLinked }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [showLinkLead, setShowLinkLead] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/tenant/voice/calls/${callId}`).then(({ data }) => {
      if (!cancelled) setDetail(data);
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [callId]);

  useEffect(() => load(), [load]);

  const handleTransfer = async () => {
    setTransferring(true);
    try {
      await api.post(`/tenant/voice/calls/${callId}/transfer`);
      toast.success('Transfiriendo la llamada a un vendedor…');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTransferring(false); }
  };

  const dur = (s) => s != null ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '-';
  const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '-';

  if (loading) return <div className="flex-1 flex items-center justify-center"><PantallaCargando texto="Cargando conversaciones…" /></div>;
  if (!detail) return <div className="flex-1 flex items-center justify-center text-sm text-[#94A3B8]">No se pudo cargar la llamada</div>;

  const statusCfg = CALL_STATUS_CONFIG[detail.status] || CALL_STATUS_CONFIG.completed;

  return (
    <div className="flex-1 overflow-y-auto p-4 bg-[#F8FAFC] space-y-3" data-testid="call-detail-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
        <span className="text-xs text-[#64748B]">{detail.direction === 'inbound' ? 'Entrante' : 'Saliente'} · {dur(detail.duration_seconds)} · {fmt(detail.started_at)}</span>
        {detail.status === 'in_progress' && (
          <Button size="sm" variant="outline" onClick={handleTransfer} disabled={transferring}
            className="ml-auto gap-1.5" data-testid="transfer-to-salesperson-btn">
            <ArrowRightLeft className="w-3.5 h-3.5" /> {transferring ? 'Transfiriendo…' : 'Transferir a vendedor'}
          </Button>
        )}
      </div>
      {detail.status === 'in_progress' && (
        <p className="text-[11px] text-[#94A3B8]">
          Conecta esta llamada en curso con el vendedor de guardia o el asignado al lead — la IA sigue en línea hasta que el vendedor responde.
        </p>
      )}

      {/* Contacto de la llamada — una llamada al número general llega sin
          dueño; aquí se puede atribuir para que quede en su historial. */}
      {detail.crm_lead_id ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-2 flex-wrap" data-testid="call-linked-lead">
          <span className="text-xs font-medium text-[#64748B]">Contacto</span>
          <a href={`/tenant/crm/contacts/${detail.crm_lead_id}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded border border-blue-200 hover:bg-blue-100"
            data-testid="call-lead-chip">
            <ExternalLink className="w-3 h-3" /> {detail.lead_name || 'Ver ficha del contacto'}
          </a>
          {isOwnerAdmin && (
            <Button variant="ghost" size="sm" onClick={() => setShowLinkLead(true)} className="ml-auto gap-1.5 text-xs"
              data-testid="change-linked-lead-btn" title="Vincular esta llamada a otro contacto">
              <Link2 className="w-3.5 h-3.5" /> Cambiar contacto
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="call-unlinked-lead">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-amber-800">Esta llamada no está vinculada a ningún contacto</p>
              <p className="text-[11px] text-amber-700 mt-0.5">
                No sabemos de quién fue. Vincúlala con un contacto de tu lista para que la llamada, su resumen y su grabación queden en su historial.
              </p>
              {isOwnerAdmin ? (
                <div className="flex items-center gap-1 mt-2">
                  <Button size="sm" variant="outline" onClick={() => setShowLinkLead(true)}
                    className="gap-1.5 bg-white" data-testid="link-lead-btn">
                    <UserPlus className="w-3.5 h-3.5" /> Vincular a un contacto
                  </Button>
                  <HelpTooltip text="Busca a la persona en tu lista de contactos y guarda esta llamada en su ficha. Es útil cuando alguien marca a tu número general y todavía no sabemos quién es. Si te equivocas, puedes cambiar el contacto después." />
                </div>
              ) : (
                <p className="text-[11px] text-amber-700 mt-2">Pídele a un administrador o al dueño de la cuenta que la vincule.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {detail.recording_url && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
          <span className="text-xs font-medium text-[#64748B]">Grabación</span>
          <audio controls className="w-full mt-2" src={detail.recording_url} data-testid="voice-call-recording-player">Tu navegador no soporta audio.</audio>
        </div>
      )}
      {detail.summary_text && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
          <span className="text-xs font-medium text-[#64748B]">Resumen IA</span>
          <p className="text-sm text-[#0F172A] mt-1">{detail.summary_text}</p>
        </div>
      )}
      {detail.transcripts?.length > 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
          <span className="text-xs font-medium text-[#64748B]">Transcripción</span>
          <div className="space-y-1 mt-2">
            {detail.transcripts.map((t, i) => (
              <p key={i} className="text-sm"><span className="font-medium text-[#334155]">{t.speaker}:</span> {t.text}</p>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-[#94A3B8] text-center py-6">Sin transcripción disponible para esta llamada.</p>
      )}
      <LinkLeadModal open={showLinkLead} onClose={() => setShowLinkLead(false)} callId={callId}
        onLinked={() => { load(); onLeadLinked?.(); }} />
    </div>
  );
}

// Sprint (wall comments -> Inbox) — context card for a public-comment
// thread: which post/media it's on, so an agent isn't replying blind.
// Degrades to a text-only card if the snapshot fetch failed or the
// source post/media was deleted (extra_data.source_unavailable).
function PostContextCard({ extraData, channel }) {
  const platform = channel === 'instagram_wall' ? 'Instagram' : 'Facebook';
  const d = extraData || {};
  if (!d.source_content_id) return null;
  return (
    <div className="mx-4 mt-3 flex gap-3 border border-[#E2E8F0] rounded-lg p-3 bg-white" data-testid="post-context-card">
      {d.source_thumbnail && !d.source_unavailable && (
        <img src={d.source_thumbnail} alt="" className="w-14 h-14 rounded object-cover shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-[#0F172A]">Comentario en una publicación de {platform}</p>
        {d.source_unavailable ? (
          <p className="text-xs text-[#94A3B8] mt-0.5">Publicación no disponible</p>
        ) : (
          <>
            {d.source_caption && <p className="text-xs text-[#64748B] mt-0.5 line-clamp-2">{d.source_caption}</p>}
            <div className="flex items-center gap-2 mt-1 text-[10px] text-[#94A3B8]">
              {d.source_account_name && <span>{d.source_account_name}</span>}
              {d.source_published_at && <span>{new Date(d.source_published_at).toLocaleDateString('es-MX')}</span>}
            </div>
          </>
        )}
        {d.source_permalink && (
          <a href={d.source_permalink} target="_blank" rel="noreferrer"
            className="inline-block mt-1.5 text-xs font-medium text-blue-600 hover:underline">Ver publicación</a>
        )}
      </div>
    </div>
  );
}

function ChatPanel({ group, onBack, onUpdate, onConversationUpdate, onTakeControlLock, onActiveConversationChange, onComposingChange }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [activeChannelId, setActiveChannelId] = useState(group?.primary_conversation_id);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  // Reiniciar al canal más reciente SOLO si el canal que el agente tiene abierto
  // ya no existe en la tarjeta. Antes se reiniciaba con cada cambio de group_key,
  // y como el group_key MUTA (una conversación adquiere lead/contacto, o dos leads
  // colapsan), eso arrancaba al agente a otro hilo mientras escribía. Ahora, si su
  // conversación sigue presente, se queda en ELLA pase lo que pase con el group_key.
  useEffect(() => {
    const stillHere = group?.channels?.some(ch => ch.conversation_id === activeChannelId);
    if (!stillHere) setActiveChannelId(group?.primary_conversation_id);
  }, [group, activeChannelId]);

  // Reportar a la página el conversation_id realmente abierto (ancla estable),
  // y si el agente está redactando (para que el polling no re-seleccione encima).
  useEffect(() => { if (activeChannelId) onActiveConversationChange?.(activeChannelId); }, [activeChannelId, onActiveConversationChange]);

  // "Redactando": congela la re-selección del polling mientras hay texto. Se
  // limpia al desmontar (cerrar/cambiar tarjeta) — no en cada tecla, para no
  // parpadear el flag.
  useEffect(() => { onComposingChange?.(text.trim().length > 0); }, [text, onComposingChange]);
  useEffect(() => () => onComposingChange?.(false), [onComposingChange]);

  const activeChannel = group?.channels?.find(ch => ch.conversation_id === activeChannelId) || group?.channels?.[0];
  const conversation = group && activeChannel ? {
    id: activeChannel.conversation_id,
    channel: activeChannel.channel,
    contact_name: group.contact_name,
    phone_normalized: group.phone_normalized,
    crm_lead_id: group.crm_lead_id,
    status: activeChannel.status,
    ai_mode: activeChannel.ai_mode,
    assigned_to_user_id: activeChannel.assigned_to_user_id,
    assigned_user_name: activeChannel.assigned_user_name,
    extra_data: activeChannel.extra_data,
  } : null;

  const isAssigned = conversation?.assigned_to_user_id === user?.id;
  const isOwnerAdmin = user?.tenant_role === 'tenant_owner' || user?.tenant_role === 'tenant_admin';
  const canReply = isAssigned || isOwnerAdmin;
  const canTransfer = isAssigned || isOwnerAdmin;
  // Sprint 58.1 (hotfix) — una llamada folded-in no tiene hilo de mensajes ni
  // acciones de conversación (tomar control/pasar/cerrar/resumen) — esas
  // pegan a endpoints de /tenant/inbox/conversations/{id}/... que no existen
  // para un conversation_id con prefijo 'call:'.
  const isVoice = conversation?.channel === 'voice';
  const isWallComment = conversation?.channel === 'facebook_wall' || conversation?.channel === 'instagram_wall';

  // Ver nota del incidente 2026-10-03 arriba (postConUnReintento).
  const loadInFlightRef = useRef(null); // conversation.id con una vuelta en vuelo
  const pollFailsRef = useRef(0);
  const pollNextAtRef = useRef(0);
  const lastReadKeyRef = useRef(null);

  const loadMessages = useCallback(async ({ silent = false } = {}) => {
    if (!conversation || isVoice) return;
    const convId = conversation.id;
    if (loadInFlightRef.current === convId) return; // no encimar vueltas del mismo chat
    loadInFlightRef.current = convId;
    if (!silent) setLoading(true);
    let ok = true;
    let data = null;
    try {
      ({ data } = await api.get(
        `/tenant/inbox/conversations/${conversation.id}/messages?tail=true&limit=200`,
        silent ? { silencioso: true } : undefined,
      ));
      setMessages(data);
    } catch { ok = false; }
    if (!silent) setLoading(false);
    // /read solo cuando hay algo nuevo que marcar (o al abrir), no cada 5 s:
    // cada /read es un UPDATE de la fila de la conversación.
    const ultimo = Array.isArray(data) && data.length ? (data[data.length - 1]?.id ?? data.length) : 'vacio';
    const readKey = `${conversation.id}:${ultimo}`;
    if (ok && readKey !== lastReadKeyRef.current) {
      try {
        await postConUnReintento(
          `/tenant/inbox/conversations/${conversation.id}/read`, undefined,
          silent ? { silencioso: true } : undefined,
        );
        lastReadKeyRef.current = readKey;
      } catch { ok = false; }
    }
    if (ok) {
      pollFailsRef.current = 0;
      pollNextAtRef.current = 0;
    } else {
      pollFailsRef.current += 1;
      const espera = Math.min(POLL_MS * 2 ** pollFailsRef.current, POLL_MAX_BACKOFF_MS);
      pollNextAtRef.current = Date.now() + espera;
    }
    if (loadInFlightRef.current === convId) loadInFlightRef.current = null;
  }, [conversation?.id]);

  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const el = messagesContainerRef.current;
      const nearBottom = el
        ? el.scrollHeight - el.scrollTop - el.clientHeight < 120
        : true;
      if (nearBottom || prevMessageCountRef.current === 0) {
        messagesEndRef.current?.scrollIntoView({ behavior: prevMessageCountRef.current === 0 ? 'auto' : 'smooth' });
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // S28 P0: Polling every 5s for real-time messages (silent refresh — no loading flicker)
  // 2026-10-03: con backoff tras fallos y sin encimar vueltas (ver loadMessages).
  useEffect(() => {
    if (!conversation?.id) return;
    pollFailsRef.current = 0;
    pollNextAtRef.current = 0;
    const interval = setInterval(() => {
      if (Date.now() < pollNextAtRef.current) return;
      loadMessages({ silent: true });
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [conversation?.id, loadMessages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    // Guarda anti-envío-al-lead-equivocado: si el canal que el agente tiene
    // abierto ya no existe en la tarjeta (la conversación se movió/fusionó bajo
    // el pie), NO se manda a channels[0] por defecto — se avisa y se aborta.
    const targetId = activeChannelId;
    const stillHere = group?.channels?.some(ch => ch.conversation_id === targetId);
    if (!targetId || !stillHere) {
      toast.error('La conversación cambió; ábrela de nuevo antes de enviar.');
      return;
    }
    setSending(true);
    try {
      await api.post(`/tenant/inbox/conversations/${targetId}/reply`, { text: text.trim() });
      setText(''); setSuggestion('');
      await loadMessages();
      onUpdate?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSending(false);
  };

  const handleSuggest = async () => {
    setSuggesting(true); setSuggestion('');
    try {
      const { data } = await api.post(`/tenant/inbox/conversations/${conversation.id}/suggest-reply`);
      setSuggestion(data.suggestion || '');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSuggesting(false);
  };

  // 2026-10-03: una acción a la vez (sin doble clic en bucle) y máximo 1 reintento.
  const [actionPending, setActionPending] = useState(false);
  const actionPendingRef = useRef(false);
  const handleAction = async (action) => {
    if (actionPendingRef.current) return;
    actionPendingRef.current = true;
    setActionPending(true);
    try {
      const { data } = await postConUnReintento(`/tenant/inbox/conversations/${conversation.id}/${action}`);
      if (data && (action === 'take-control' || action === 'takeover')) {
        onTakeControlLock?.();
        onConversationUpdate?.(conversation.id, { ...data, ai_mode: data.ai_mode || 'manual' });
        toast.success('Tienes el control');
        return;
      }
      if (data && action === 'release-control') {
        onConversationUpdate?.(conversation.id, { ...data, ai_mode: data.ai_mode || 'auto' });
        toast.success('Tu asistente IA vuelve a responder');
        return;
      }
      if (data?.status) {
        onConversationUpdate?.(conversation.id, data);
      }
      onUpdate?.();
      toast.success(action === 'close' ? 'Conversación cerrada' : action === 'reopen' ? 'Conversación reabierta' : 'Listo');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      actionPendingRef.current = false;
      setActionPending(false);
    }
  };

  const [reactivating, setReactivating] = useState(false);
  const handleReactivate = async () => {
    if (reactivating || !activeChannelId) return;
    setReactivating(true);
    try {
      const { data } = await api.post(`/tenant/inbox/conversations/${activeChannelId}/reactivate`);
      toast.success(data?.message || 'Aviso de reactivación enviado');
      onUpdate?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setReactivating(false);
  };

  if (!group || !conversation) return null;

  return (
    <div className="flex flex-col h-full" data-testid="chat-panel">
      <ChannelTabs channels={group.channels} activeId={activeChannelId} onSelect={setActiveChannelId} />
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E8F0] bg-white">
        <Button variant="ghost" size="sm" onClick={onBack} className="md:hidden" data-testid="chat-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-[#0F172A] truncate">{conversation.contact_name || conversation.phone_normalized || 'Contacto'}</h3>
          <div className="flex items-center gap-2 mt-0.5">
            <StatusBadge status={conversation.status} />
            {isWallComment && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200" data-testid="public-reply-indicator">
                Respuesta pública
              </span>
            )}
            {conversation.assigned_user_name && <span className="text-[10px] text-[#64748B]">Asignado a {conversation.assigned_user_name}</span>}
          </div>
        </div>
        {/* La barra de acciones NO se encoge: `flex-shrink-0`.
            Sin eso, el titulo de al lado (flex-1) le ganaba el espacio y los
            botones se cortaban hasta desaparecer. Al cambiar de canal cambia
            cuantos botones hay, volvian a caber, y parecia un fallo de zoom.
            `flex-wrap` para que en pantallas angostas bajen de renglón en vez
            de quedar fuera de la vista: un boton que no se alcanza es un boton
            que no existe. */}
        <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
          {conversation.ai_mode === 'auto' && (
            <Button variant="outline" size="sm" onClick={() => handleAction('take-control')} disabled={actionPending} data-testid="inbox-takeover-btn"
              title="Tu asistente IA está respondiendo. Toma el control para responder tú.">
              <Hand className="w-3.5 h-3.5 mr-1" /> Tomar conversación
            </Button>
          )}
          {conversation.ai_mode === 'manual' && conversation.status !== 'closed' && (
            <Button variant="outline" size="sm" onClick={() => handleAction('release-control')} disabled={actionPending} data-testid="inbox-release-control-btn"
              title="Devuelve esta conversación a tu asistente IA para que vuelva a responder automáticamente.">
              <Bot className="w-3.5 h-3.5 mr-1" /> Devolver a IA
            </Button>
          )}
          {!isVoice && canTransfer && conversation.status !== 'closed' && (
            <Button variant="outline" size="sm" onClick={() => setShowTransfer(true)} data-testid="transfer-btn"
              title="Pasa esta conversación a otro vendedor de tu equipo">
              <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Pasar
            </Button>
          )}
          {/* Reactivar lead: solo WhatsApp fuera de la ventana de 24h (conversación
              cerrada). Envía la plantilla utility (Aceptar/Rechazar) al lead y reabre
              ESTA conversación con su lead original. Reemplaza al viejo "Llamar con IA"
              (que usaba el lead del grupo y podía marcar al cliente equivocado). */}
          {!isVoice && conversation.channel === 'whatsapp' && conversation.status === 'closed' && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={handleReactivate} disabled={reactivating} data-testid="reactivate-lead-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> {reactivating ? 'Enviando...' : 'Reactivar lead'}
              </Button>
              <HelpTooltip text="Pasadas 24 horas, WhatsApp cierra la conversación y ya no puedes escribir texto libre. Con un clic, el sistema le manda al cliente un aviso aprobado con dos botones (Aceptar / Rechazar). Cuando el cliente toca Aceptar, la conversación se reabre por otras 24 horas — ligada a ESTE mismo lead, sin confundir clientes. Necesitas tener la plantilla de reactivación aprobada en tu WhatsApp (Configuración → WhatsApp)." />
            </div>
          )}
          {!isVoice && (
            <Button variant="outline" size="sm" onClick={() => setShowSummary(!showSummary)} data-testid="summary-toggle-btn"
              title="Ver resumen de la conversación generado por IA">
              <FileText className="w-3.5 h-3.5" />
            </Button>
          )}
          {!isVoice && (conversation.status !== 'closed' ? (
            <Button variant="outline" size="sm" onClick={() => handleAction('close')} data-testid="close-conversation-btn" title="Cerrar esta conversación">
              <CheckCircle2 className="w-3.5 h-3.5" />
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => handleAction('reopen')} data-testid="reopen-conversation-btn" title="Reabrir conversación">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          ))}
        </div>
      </div>

      {/* AI Banner */}
      {conversation.ai_mode === 'auto' && (
        <div className="px-4 py-2 bg-violet-50 border-b border-violet-200 flex items-center justify-between" data-testid="ai-banner">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-violet-600" />
            <span className="text-xs text-violet-700">Tu asistente está respondiendo</span>
          </div>
          <Button size="sm" variant="outline" onClick={() => handleAction('take-control')} disabled={actionPending} className="border-violet-300 text-violet-700 hover:bg-violet-100">
            <Hand className="w-3 h-3 mr-1" /> Tomar conversación
          </Button>
        </div>
      )}

      {isVoice ? (
        <CallDetailPanel callId={activeChannel.call_id} isOwnerAdmin={isOwnerAdmin}
          onLeadLinked={() => onUpdate?.()} />
      ) : (
        <>
          {isWallComment && <PostContextCard extraData={conversation.extra_data} channel={conversation.channel} />}

          {/* Summary Panel */}
          {showSummary && <SummaryPanel conversationId={conversation.id} extraData={conversation.extra_data} />}

          {/* Messages */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 bg-[#F8FAFC]" data-testid="messages-container">
            {loading && messages.length === 0 && <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 text-[#94A3B8] animate-spin" /></div>}
            {messages.map(m => <MessageBubble key={m.id} msg={m} />)}
            <div ref={messagesEndRef} />
          </div>

          {/* AI Suggestion */}
          {suggestion && (
            <div className="mx-4 mb-2 p-3 bg-violet-50 border border-violet-200 rounded-lg" data-testid="ai-suggestion">
              <div className="flex items-center gap-1 text-[10px] text-violet-600 font-medium mb-1"><Sparkles className="w-3 h-3" /> Sugerencia IA</div>
              <p className="text-sm text-[#0F172A]">{suggestion}</p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => { setText(suggestion); setSuggestion(''); }} data-testid="use-suggestion-btn">Usar</Button>
                <Button size="sm" variant="ghost" onClick={() => setSuggestion('')}><X className="w-3 h-3" /></Button>
              </div>
            </div>
          )}

          {/* Input */}
          {conversation.status !== 'closed' && (
            <div className="border-t border-[#E2E8F0] p-3 bg-white" data-testid="chat-input-area">
              {!canReply ? (
                <p className="text-xs text-[#94A3B8] text-center py-2">Solo el vendedor asignado puede responder. Pide una transferencia.</p>
              ) : (
                <>
                  {isWallComment && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2" data-testid="public-reply-warning">
                      Esta respuesta se publicará públicamente en {conversation.channel === 'instagram_wall' ? 'Instagram' : 'Facebook'}, visible para cualquiera que vea la publicación.
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleSuggest} disabled={suggesting} data-testid="suggest-reply-btn"
                      title="Pedir sugerencia IA"><Sparkles className={`w-4 h-4 ${suggesting ? 'animate-pulse text-violet-500' : 'text-[#64748B]'}`} /></Button>
                    <Input data-testid="chat-input" value={text} onChange={e => setText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()} placeholder={isWallComment ? 'Escribe la respuesta pública...' : 'Escribe un mensaje...'} className="flex-1" />
                    <Button size="sm" onClick={handleSend} disabled={sending || !text.trim()} data-testid="send-message-btn"><Send className="w-4 h-4" /></Button>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      <TransferModal open={showTransfer} onClose={() => setShowTransfer(false)} conversationId={conversation.id} onDone={() => { onUpdate?.(); loadMessages(); }} />
    </div>
  );
}

/* ── Help ───────────────────────────────────────────────────── */
function InboxHelp({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>¿Cómo funciona Conversaciones?</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-[#64748B]">
          <p><strong>Conversaciones</strong> es donde ves todos los mensajes de WhatsApp de tus clientes. Cuando alguien te escribe por una captación o directamente, aparece aquí.</p>
          <p><strong>Tomar conversación:</strong> Si tu asistente IA está atendiendo, puedes tomar el control para responder tú directamente.</p>
          <p><strong>Pasar a vendedor:</strong> Transfiere el chat a otro compañero de tu equipo que esté disponible para atender.</p>
          <p><strong>Ver resumen:</strong> La IA genera un resumen corto de la conversación para que sepas rápidamente de qué se trata.</p>
          <p><strong>Llamadas sin contacto:</strong> Cuando alguien marca a tu número general, la llamada llega sin dueño. Ábrela y usa <em>Vincular a un contacto</em> para guardarla en la ficha de esa persona, junto con su resumen y su grabación. Solo el dueño de la cuenta o un administrador pueden hacerlo.</p>
          <p><strong>Estados:</strong> <em>Sin responder</em> = nadie ha contestado. <em>IA atendiendo</em> = tu asistente respondió. <em>Tú respondiste</em> = un agente contestó. <em>Cerrado</em> = conversación finalizada.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* Configuración de reactivación (Etapa 2): elige la plantilla utility aprobada
   que usa el botón "Reactivar lead", con la guía para darla de alta en Gupshup. */
function ReactivationConfigModal({ open, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setCfg(null);
    (async () => {
      try {
        const { data } = await api.get('/tenant/inbox/reactivation-config');
        setCfg(data); setSel(data?.reactivation_template_id || '');
      } catch { setCfg({ templates: [] }); }
    })();
  }, [open]);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/tenant/inbox/reactivation-config', { reactivation_template_id: sel || null });
      toast.success('Plantilla de reactivación guardada');
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo guardar'); }
    setSaving(false);
  };
  const tpls = cfg?.templates || [];
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="reactivation-config-modal">
        <DialogHeader><DialogTitle className="text-lg font-bold text-[#0F172A]">Reactivar clientes tras 24 horas</DialogTitle></DialogHeader>
        <p className="text-xs text-[#64748B]">
          Pasadas 24 horas sin respuesta, WhatsApp cierra la conversación y ya no puedes escribir texto libre.
          El botón <b>“Reactivar lead”</b> le manda al cliente un aviso aprobado con botones <b>Aceptar / Rechazar</b>;
          al aceptar, la conversación se reabre por otras 24 horas con su mismo lead. Elige aquí qué plantilla se usa.
        </p>
        {cfg === null ? (
          <p className="text-sm text-[#94A3B8] py-4">Cargando…</p>
        ) : tpls.length === 0 ? (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5 text-amber-800" data-testid="reactivation-no-template">
            <p>Aún no tienes una plantilla <b>“utility” aprobada</b> para esto. Regístrala una vez en tu portal de Gupshup:</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Gupshup → tu app de WhatsApp → <b>Templates</b> → <b>Add / New</b>.</li>
              <li>Categoría <b>Utility</b>, idioma <b>Español</b>.</li>
              <li>Cuerpo: <i>“Tienes un mensaje importante de {'{{1}}'}”</i> (la variable {'{{1}}'} es el nombre de tu negocio).</li>
              <li>Agrega dos <b>botones de respuesta rápida</b>: <b>Aceptar</b> y <b>Rechazar</b>.</li>
              <li>Envíala a aprobar. Cuando Meta la apruebe (suele tardar minutos), vuelve aquí y aparecerá para seleccionarla.</li>
            </ol>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-xs font-medium text-[#334155]">Plantilla de reactivación</label>
            <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="reactivation-template-select"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm">
              <option value="">— Ninguna (el botón pedirá configurarla) —</option>
              {tpls.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.language})</option>)}
            </select>
            <div className="flex justify-end pt-1">
              <Button size="sm" onClick={save} disabled={saving} data-testid="reactivation-save-btn">
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Main Inbox ────────────────────────────────────────────── */
export default function Inbox({ statusFilter }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [stats, setStats] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showReactCfg, setShowReactCfg] = useState(false);
  const [channelFilter, setChannelFilter] = useState('');
  const takeControlLockUntil = useRef(0);
  // Ancla ESTABLE de la conversación abierta (conversation_id, UUID inmutable).
  // El polling reconcilia la tarjeta por esta ancla, NO por group_key (que muta
  // cuando una conversación adquiere lead/contacto, o cuando dos leads colapsan
  // en la misma tarjeta): así el agente nunca salta de lead mientras trabaja.
  const activeConvRef = useRef(null);
  // Mientras el agente está redactando, el polling no re-selecciona la tarjeta.
  const composingRef = useRef(false);

  const isOwnerAdmin = user?.tenant_role === 'tenant_owner' || user?.tenant_role === 'tenant_admin';

  const lockTakeControl = useCallback(() => {
    takeControlLockUntil.current = Date.now() + 15000;
  }, []);

  // Selección de tarjeta desde la lista: fija el ancla a su conversación activa.
  const handleSelectCard = useCallback((card) => {
    activeConvRef.current = card?.primary_conversation_id
      || card?.channels?.[0]?.conversation_id || null;
    setSelected(card);
  }, []);

  const handleCloseCard = useCallback(() => {
    activeConvRef.current = null;
    composingRef.current = false;
    setSelected(null);
  }, []);

  // Estables (useCallback) para no re-suscribir los efectos del ChatPanel en
  // cada render del padre.
  const handleActiveConvChange = useCallback((cid) => { activeConvRef.current = cid; }, []);
  const handleComposingChange = useCallback((v) => { composingRef.current = v; }, []);

  // Sprint 58.1 — merges an action's result (take-control/release-control/
  // close/reopen) into the SPECIFIC channel of the selected contact card,
  // instead of overwriting the whole card — a card can have channels other
  // than the one the action touched.
  const handleConversationUpdate = useCallback((conversationId, patch) => {
    setSelected(prev => {
      if (!prev) return prev;
      const channels = prev.channels.map(ch => ch.conversation_id === conversationId ? { ...ch, ...patch } : ch);
      return { ...prev, channels };
    });
  }, []);

  const loadConvInFlightRef = useRef(false);
  const loadConversations = useCallback(async (newOffset = 0, append = false, { silent = false } = {}) => {
    // 2026-10-03: el sondeo de fondo no se encima a una vuelta aún en vuelo.
    if (silent && loadConvInFlightRef.current) return;
    loadConvInFlightRef.current = true;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (filter === 'mine') params.set('assigned_to', user?.id);
      if (filter === 'unattended') params.set('filter_type', 'unattended');
      if (filter === 'ai') params.set('filter_type', 'ai');
      if (search) params.set('search', search);
      if (channelFilter) params.set('channel', channelFilter);
      params.set('limit', '50');
      params.set('offset', String(newOffset));

      const { data } = await api.get(`/tenant/inbox/conversations/grouped?${params}`, silent ? { silencioso: true } : undefined);
      const newItems = data.items || [];
      setItems(prev => append ? [...prev, ...newItems] : newItems);
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
      setOffset(newOffset + newItems.length);

      // Reconciliar la tarjeta abierta con los datos frescos SIN cambiar de lead.
      // Se ancla a la conversación que el agente tiene abierta (UUID inmutable),
      // no al group_key: si la conversación sigue existiendo en alguna tarjeta,
      // nos quedamos en ELLA aunque su group_key haya mutado o haya colapsado con
      // otro lead. Si no aparece (filtrada/ausente), se deja la tarjeta actual —
      // nunca se salta a otra. No se toca mientras se redacta ni durante el lock
      // de "tomar control". Se usan refs (siempre actuales) para no depender de
      // un `selected` capturado en el closure del callback memoizado.
      const anchor = activeConvRef.current;
      if (anchor && !append && !composingRef.current && Date.now() >= takeControlLockUntil.current) {
        const updated = newItems.find(c => c.channels?.some(ch => ch.conversation_id === anchor));
        if (updated) setSelected(updated);
      }
    } catch {}
    loadConvInFlightRef.current = false;
    if (!silent) setLoading(false);
  }, [statusFilter, filter, search, channelFilter, user?.id]);

  const loadStats = useCallback(async () => {
    if (!isOwnerAdmin) return;
    try {
      const { data } = await api.get('/tenant/inbox/stats');
      setStats(data);
    } catch {}
  }, [isOwnerAdmin]);

  useEffect(() => { loadConversations(); loadStats(); }, [loadConversations, loadStats]);

  // Deep-link: /tenant/inbox?c={conversationId} (from CRM "Abrir en Inbox") —
  // busca la tarjeta de contacto que contenga ese conversation_id en
  // cualquiera de sus canales, ya no coincide 1:1 con el group_key.
  useEffect(() => {
    const convId = searchParams.get('c');
    if (!convId || items.length === 0) return;
    const match = items.find(c => c.channels.some(ch => ch.conversation_id === convId));
    if (match) {
      // Ancla a la conversación exacta del deep-link (no a la primaria del grupo).
      activeConvRef.current = convId;
      setSelected(match);
      searchParams.delete('c');
      setSearchParams(searchParams, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  // S28 P0: Polling every 10s for conversation list updates
  useEffect(() => {
    const interval = setInterval(() => { loadConversations(0, false, { silent: true }); }, 10000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => loadConversations(), 400);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div data-testid="inbox-page" className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}>
      <div className="flex h-full">
        <div className={`w-full md:w-[380px] border-r border-[#E2E8F0] flex-shrink-0 ${selected ? 'hidden md:flex md:flex-col' : 'flex flex-col'}`}>
          {/* Title bar */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0]">
            <h2 className="text-base font-semibold text-[#0F172A]">Conversaciones</h2>
            <div className="flex items-center gap-0.5">
              {isOwnerAdmin && (
                <Button variant="ghost" size="sm" onClick={() => setShowReactCfg(true)} data-testid="inbox-reactivation-cfg-btn" aria-label="Configurar reactivación tras 24h" title="Configurar reactivación de clientes tras 24 horas"><Settings className="w-4 h-4 text-[#94A3B8]" /></Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setShowHelp(true)} data-testid="inbox-help-btn" aria-label="¿Cómo funciona?"><HelpCircle className="w-4 h-4 text-[#94A3B8]" /></Button>
            </div>
          </div>
          <ConversationList items={items} selected={selected} onSelect={handleSelectCard} loading={loading}
            onRefresh={() => loadConversations()} filter={filter} setFilter={setFilter} search={search} setSearch={setSearch}
            total={total} hasMore={hasMore} onLoadMore={() => loadConversations(offset, true)}
            stats={stats} onStatsFilter={setFilter} isOwnerAdmin={isOwnerAdmin}
            channelFilter={channelFilter} setChannelFilter={setChannelFilter} />
        </div>
        <div className={`flex-1 ${!selected ? 'hidden md:flex' : 'flex'} flex-col`}>
          {selected ? (
            <ChatPanel group={selected} onBack={handleCloseCard} onUpdate={() => { loadConversations(); loadStats(); }} onConversationUpdate={handleConversationUpdate} onTakeControlLock={lockTakeControl}
              onActiveConversationChange={handleActiveConvChange}
              onComposingChange={handleComposingChange} />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-[#F8FAFC]" data-testid="empty-chat-panel">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-[#64748B]">Selecciona una conversación</p>
              </div>
            </div>
          )}
        </div>
      </div>
      <InboxHelp open={showHelp} onClose={() => setShowHelp(false)} />
      <ReactivationConfigModal open={showReactCfg} onClose={() => setShowReactCfg(false)} />
    </div>
  );
}
